// components/superAdmin/SuperAdminOverviewTab.js
import React, { useMemo } from 'react';
import { Box, Typography, LinearProgress, Chip, Skeleton } from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { SA, cardSx } from './theme';
import { daysUntil } from './featureFlags';

const ROLE_LABELS = { manager: 'مدير', employee: 'موظف', teacher: 'معلم', parent: 'ولي أمر', student: 'طالب' };
const INACTIVITY_DAYS = 30;

const StatTile = ({ icon, label, value, sub, tone = SA.primary, soft = SA.primarySoft, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      ...cardSx,
      p: 2.5,
      display: 'flex',
      gap: 2,
      alignItems: 'center',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color .15s',
      '&:hover': onClick ? { borderColor: tone } : undefined,
    }}
  >
    <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: soft, color: tone, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, color: SA.muted, fontFamily: SA.font }}>{label}</Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: SA.text, lineHeight: 1.2, fontFamily: SA.font }}>{value ?? '—'}</Typography>
      {sub && <Typography sx={{ fontSize: 12, color: SA.muted, fontFamily: SA.font }}>{sub}</Typography>}
    </Box>
  </Box>
);

const SuperAdminOverviewTab = ({ stats, accounts, settings, onOpenAccounts }) => {
  const warningDays = settings?.expiry_warning_days ?? 14;

  // Every school that needs a super-admin's attention, with the reason
  const attention = useMemo(() => {
    const items = [];
    accounts.forEach((acc) => {
      const name = acc.school_name || acc.name || '—';
      const left = daysUntil(acc.subscription_end_date);
      if (acc.is_active && left !== null && left < 0) {
        items.push({ acc, name, tone: 'error', reason: `انتهى الاشتراك منذ ${-left} يوم`, rank: 0 });
      } else if (acc.is_active && left !== null && left <= warningDays) {
        items.push({ acc, name, tone: 'warning', reason: `الاشتراك ينتهي خلال ${left} يوم`, rank: 1 });
      }
      if (acc.max_students && acc.student_count / acc.max_students >= 0.9) {
        items.push({ acc, name, tone: 'warning', reason: `الطلاب ${acc.student_count}/${acc.max_students}`, rank: 2 });
      }
      if (acc.max_users && acc.user_count >= acc.max_users) {
        items.push({ acc, name, tone: 'warning', reason: `وصل لحد المستخدمين (${acc.max_users})`, rank: 2 });
      }
      if (acc.is_active) {
        const idle = acc.last_activity
          ? Math.floor((Date.now() - new Date(acc.last_activity)) / 86400000)
          : null;
        if (idle === null || idle >= INACTIVITY_DAYS) {
          items.push({ acc, name, tone: 'default', reason: idle === null ? 'لا يوجد أي نشاط' : `بدون نشاط منذ ${idle} يوم`, rank: 3 });
        }
      }
    });
    return items.sort((a, b) => a.rank - b.rank);
  }, [accounts, warningDays]);

  const roleTotal = stats ? Object.values(stats.users_by_role || {}).reduce((a, b) => a + b, 0) : 0;

  if (!stats) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={96} sx={{ borderRadius: 3 }} />)}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
        <StatTile
          icon={<SchoolOutlinedIcon />}
          label="المدارس"
          value={stats.total_accounts}
          sub={`${stats.active_accounts} نشطة · ${stats.inactive_accounts} موقوفة`}
          onClick={() => onOpenAccounts({ filter: 'all' })}
        />
        <StatTile
          icon={<PeopleOutlineIcon />}
          label="المستخدمون"
          value={stats.total_users}
          sub={`${stats.active_users} نشط · ${stats.inactive_users} معطّل`}
          tone={SA.success}
          soft={SA.successSoft}
        />
        <StatTile
          icon={<HourglassBottomIcon />}
          label="اشتراكات قريبة الانتهاء"
          value={stats.expiring_soon_accounts ?? 0}
          sub={`خلال ${warningDays} يوم`}
          tone={SA.warnInk}
          soft={SA.warnBg}
          onClick={() => onOpenAccounts({ filter: 'expiring' })}
        />
        <StatTile
          icon={<EventBusyOutlinedIcon />}
          label="اشتراكات منتهية"
          value={stats.expired_accounts ?? 0}
          sub="تسجيل الدخول ممنوع"
          tone={SA.danger}
          soft={SA.dangerSoft}
          onClick={() => onOpenAccounts({ filter: 'expired' })}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2, alignItems: 'start' }}>
        {/* Needs attention */}
        <Box sx={{ ...cardSx, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${SA.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 800, color: SA.text, fontFamily: SA.font }}>تحتاج إلى متابعة</Typography>
            <Chip size="small" label={attention.length} />
          </Box>
          {attention.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: SA.muted }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: SA.success, mb: 1 }} />
              <Typography sx={{ fontFamily: SA.font }}>كل المدارس بحالة جيدة</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {attention.map((item, i) => (
                <Box
                  key={`${item.acc.id}-${i}`}
                  onClick={() => onOpenAccounts({ openId: item.acc.id })}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, cursor: 'pointer',
                    borderBottom: `1px solid ${SA.border}`, '&:last-child': { borderBottom: 0 },
                    '&:hover': { bgcolor: SA.bg },
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: SA.text, flexGrow: 1, fontFamily: SA.font }}>{item.name}</Typography>
                  <Chip size="small" color={item.tone} variant={item.tone === 'default' ? 'outlined' : 'filled'} label={item.reason} />
                  <ChevronLeftIcon sx={{ color: SA.muted }} fontSize="small" />
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Users by role */}
        <Box sx={{ ...cardSx, p: 2.5 }}>
          <Typography sx={{ fontWeight: 800, color: SA.text, mb: 2, fontFamily: SA.font }}>المستخدمون حسب الصلاحية</Typography>
          {Object.entries(stats.users_by_role || {}).map(([role, count]) => (
            <Box key={role} sx={{ mb: 1.75 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 14, color: SA.text, fontFamily: SA.font }}>{ROLE_LABELS[role] || role}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: SA.text, fontFamily: SA.font }}>{count}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={roleTotal ? (count / roleTotal) * 100 : 0}
                sx={{ height: 6, borderRadius: 3, bgcolor: SA.primarySoft, '& .MuiLinearProgress-bar': { bgcolor: SA.primary, borderRadius: 3 } }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SuperAdminOverviewTab;
